/**
 * Copyright 2017 Google Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
'use strict';
import { ErrorFactory } from '@firebase/util';
import Errors from '../models/errors';
import TokenManager from '../models/token-manager';
import NOTIFICATION_PERMISSION from '../models/notification-permission';
var SENDER_ID_OPTION_NAME = 'messagingSenderId';
var ControllerInterface = /** @class */ (function () {
 /**
 * An interface of the Messaging Service API
 * @param {!firebase.app.App} app
 */
 function ControllerInterface(app) {
 var _this = this;
 this.errorFactory_ = new ErrorFactory('messaging', 'Messaging', Errors.map);
 if (!app.options[SENDER_ID_OPTION_NAME] ||
 typeof app.options[SENDER_ID_OPTION_NAME] !== 'string') {
 throw this.errorFactory_.create(Errors.codes.BAD_SENDER_ID);
 }
 this.messagingSenderId_ = app.options[SENDER_ID_OPTION_NAME];
 this.tokenManager_ = new TokenManager();
 this.app = app;
 this.INTERNAL = {};
 this.INTERNAL.delete = function () { return _this.delete; };
 }
 /**
 * @export
 * @return {Promise | Promise} Returns a promise that
 * resolves to an FCM token.
 */
 ControllerInterface.prototype.getToken = function () {
 var _this = this;
 // Check with permissions
 var currentPermission = this.getNotificationPermission_();
 if (currentPermission !== NOTIFICATION_PERMISSION.granted) {
 if (currentPermission === NOTIFICATION_PERMISSION.denied) {
 return Promise.reject(this.errorFactory_.create(Errors.codes.NOTIFICATIONS_BLOCKED));
 }
 // We must wait for permission to be granted
 return Promise.resolve(null);
 }
 return this.getSWRegistration_().then(function (registration) {
 return _this.tokenManager_
 .getSavedToken(_this.messagingSenderId_, registration)
 .then(function (token) {
 if (token) {
 return token;
 }
 return _this.tokenManager_.createToken(_this.messagingSenderId_, registration);
 });
 });
 };
 /**
 * This method deletes tokens that the token manager looks after and then
 * unregisters the push subscription if it exists.
 * @export
 * @param {string} token
 * @return {Promise}
 */
 ControllerInterface.prototype.deleteToken = function (token) {
 var _this = this;
 return this.tokenManager_.deleteToken(token).then(function () {
 return _this.getSWRegistration_()
 .then(function (registration) {
 if (registration) {
 return registration.pushManager.getSubscription();
 }
 })
 .then(function (subscription) {
 if (subscription) {
 return subscription.unsubscribe();
 }
 });
 });
 };
 ControllerInterface.prototype.getSWRegistration_ = function () {
 throw this.errorFactory_.create(Errors.codes.SHOULD_BE_INHERITED);
 };
 //
 // The following methods should only be available in the window.
 //
 ControllerInterface.prototype.requestPermission = function () {
 throw this.errorFactory_.create(Errors.codes.AVAILABLE_IN_WINDOW);
 };
 /**
 * @export
 * @param {!ServiceWorkerRegistration} registration
 */
 ControllerInterface.prototype.useServiceWorker = function (registration) {
 throw this.errorFactory_.create(Errors.codes.AVAILABLE_IN_WINDOW);
 };
 /**
 * @export
 * @param {!firebase.Observer|function(*)} nextOrObserver
 * @param {function(!Error)=} optError
 * @param {function()=} optCompleted
 * @return {!function()}
 */
 ControllerInterface.prototype.onMessage = function (nextOrObserver, optError, optCompleted) {
 throw this.errorFactory_.create(Errors.codes.AVAILABLE_IN_WINDOW);
 };
 /**
 * @export
 * @param {!firebase.Observer|function()} nextOrObserver An observer object
 * or a function triggered on token refresh.
 * @param {function(!Error)=} optError Optional A function
 * triggered on token refresh error.
 * @param {function()=} optCompleted Optional function triggered when the
 * observer is removed.
 * @return {!function()} The unsubscribe function for the observer.
 */
 ControllerInterface.prototype.onTokenRefresh = function (nextOrObserver, optError, optCompleted) {
 throw this.errorFactory_.create(Errors.codes.AVAILABLE_IN_WINDOW);
 };
 //
 // The following methods are used by the service worker only.
 //
 /**
 * @export
 * @param {function(Object)} callback
 */
 ControllerInterface.prototype.setBackgroundMessageHandler = function (callback) {
 throw this.errorFactory_.create(Errors.codes.AVAILABLE_IN_SW);
 };
 //
 // The following methods are used by the service themselves and not exposed
 // publicly or not expected to be used by developers.
 //
 /**
 * This method is required to adhere to the Firebase interface.
 * It closes any currently open indexdb database connections.
 */
 ControllerInterface.prototype.delete = function () {
 return this.tokenManager_.closeDatabase();
 };
 /**
 * Returns the current Notification Permission state.
 * @private
 * @return {string} The currenct permission state.
 */
 ControllerInterface.prototype.getNotificationPermission_ = function () {
 return Notification.permission;
 };
 /**
 * @protected
 * @returns {TokenManager}
 */
 ControllerInterface.prototype.getTokenManager = function () {
 return this.tokenManager_;
 };
 return ControllerInterface;
}());
export default ControllerInterface;

//# sourceMappingURL=controller-interface.js.map



//////////////////
// WEBPACK FOOTER
// ../messaging/dist/esm/src/controllers/controller-interface.js
// module id = null
// module chunks = 